import { Dispatch, FC, SetStateAction } from 'react';

import { StyleSheet } from 'react-native';
import { Divider } from 'ucl/components';

import { IImageFragment } from 'generated/sanity-graphql';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { spacing } from 'styles/constants';

import { WalmartLandingState } from './landing-state';
import { BlockContainer } from './styled';
import { HeaderVersion, LandingPageType } from './types';
import { WalmartLandingHeader } from './walmart-landing-header';
import { WalmartLandingHeaderV2 } from './walmart-landing-header-v2';
import { WalmartLandingOffers } from './walmart-landing-offers';
import { WalmartLandingOffers as WalmartLandingOffersV2 } from './walmart-landing-offers/landing-offers-v2';
import { WalmartMembership } from './walmart-membership';
import { WalmartRoyalPerksMember } from './walmart-royal-perks-member';
import { WalmartRoyalPerksMemberV2 } from './walmart-royal-perks-member-v2';
import { WalmartTerms } from './walmart-terms';

interface WalmartLandingContentProps {
  authzCode?: string | string[];
  enableWalmartPlusAnnualTrialOffer: boolean;
  enableWalmartPlusMonthlyTrialOffer: boolean;
  isV2LandingEnabled: boolean;
  isAlreadyLinked: boolean;
  landingState: WalmartLandingState;
  landingType: string | string[];
  headerVersion: HeaderVersion;
  showLegacyHeader: boolean;
  showPerksMember: boolean;
  handleShouldRedirect?: Dispatch<SetStateAction<boolean>>;
  walmartMembershipImage?: IImageFragment | null;
}

export const WalmartLandingContent: FC<WalmartLandingContentProps> = props => {
  return props.isV2LandingEnabled ? (
    <WalmartLandingV2 {...props} />
  ) : (
    <WalmartLandingLegacy {...props} />
  );
};

const WalmartLandingLegacy: FC<WalmartLandingContentProps> = ({
  authzCode,
  enableWalmartPlusAnnualTrialOffer,
  enableWalmartPlusMonthlyTrialOffer,
  isAlreadyLinked,
  headerVersion,
  landingType,
  showLegacyHeader,
  showPerksMember,
  walmartMembershipImage,
}) => {
  const shouldDisplayMembershipOffer =
    (enableWalmartPlusAnnualTrialOffer || enableWalmartPlusMonthlyTrialOffer) &&
    !authzCode &&
    !isAlreadyLinked;

  return (
    <>
      {showLegacyHeader && <WalmartLandingHeader headerVersion={headerVersion} />}
      <WalmartLandingOffers isAlreadyLinked={isAlreadyLinked} />
      <BlockContainer>
        {shouldDisplayMembershipOffer && (
          <WalmartMembership
            {...(landingType === LandingPageType.DISTRO || isAlreadyLinked
              ? { image: walmartMembershipImage }
              : {
                  title: enableWalmartPlusMonthlyTrialOffer
                    ? t('dontHaveWalmartAccountMonthly')
                    : t('dontHaveWalmartPlusAccount'),
                  image: walmartMembershipImage,
                })}
          />
        )}
        {showPerksMember && <WalmartRoyalPerksMember />}
        <Divider mt="40px" />
        <WalmartTerms />
      </BlockContainer>
    </>
  );
};

const WalmartLandingV2: FC<WalmartLandingContentProps> = ({
  handleShouldRedirect,
  isAlreadyLinked,
  landingState,
}) => {
  const isMobile = useIsMobileBreakpoint();

  return (
    <>
      <BlockContainer
        style={[isMobile ? styles.mobileHeaderContainer : undefined, styles.container]}
      >
        <WalmartLandingHeaderV2 />
      </BlockContainer>
      <WalmartLandingOffersV2 isAlreadyLinked={isAlreadyLinked} />
      <BlockContainer style={styles.contentContainer}>
        {landingState === WalmartLandingState.LOGGED_OUT && (
          <WalmartRoyalPerksMemberV2 handleShouldRedirect={handleShouldRedirect} />
        )}
        <Divider mt="48px" mb="16px" />
        <WalmartTerms />
      </BlockContainer>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: spacing.$6,
  },
  mobileHeaderContainer: {
    maxWidth: '100%',
    paddingHorizontal: 0,
  },
  contentContainer: {
    paddingBottom: spacing.$4,
  },
});
